import {
  IDENTITY_KEY,
  SNAPSHOT_KEY,
  clearOfflineDatabase,
  idbGet,
  idbSet,
  idbSetForIdentity
} from "./idb"
import {
  OFFLINE_CACHEABLE_HEADER,
  OFFLINE_LANDING_URL,
  OFFLINE_SUBJECT_HEADER,
  canStoreNavigationResponse,
  privateCacheName,
  privateSnapshotMatchKey,
  isObsoletePrivateCache,
  isDeniedPath,
  normalizeOfflineUrl
} from "./cache_policy"
import { STATUS_EVENT } from "./register"

// 同期1回で catalog の全URLを実際にサーバでレンダリングさせる。ここの値は
// そのまま DB 負荷になるので、緩めるときは 件数 x 頻度 x 利用者数 で見積もること。
export const SYNC_INTERVAL_MS = 12 * 60 * 60 * 1000
export const MAX_SNAPSHOT_BYTES = 25 * 1024 * 1024
export const FETCH_CONCURRENCY = 1
// 単一端末の通常同期がサーバの10秒20件枠を超えない間隔。
export const FETCH_DELAY_MS = 500
export const RETRY_BACKOFF_MS = 30 * 60 * 1000
export const OFFLINE_SYNC_HEADER = "X-Firmee-Offline-Sync"
export const OFFLINE_SYNC_EVENT = "firmee:offline-sync"
export const MAX_CATALOG_MATTERS = 20
const SYNC_STATE_KEY = "sync-state"
const OPAQUE_TOKEN_PATTERN = /^[A-Za-z0-9_-]+$/
const ALLOWED_SNAPSHOT_PATH_PREFIXES = ["/matters", "/contacts", "/memos", "/schedules"]

function publishStatus(detail) {
  window.dispatchEvent(new CustomEvent(STATUS_EVENT, { detail }))
  window.dispatchEvent(new CustomEvent(OFFLINE_SYNC_EVENT))
}

export async function getIdentity() {
  return (await idbGet("identity", IDENTITY_KEY)) || { enabled: false, partition: null }
}

export async function setIdentity(identity) {
  await idbSet("identity", IDENTITY_KEY, { ...identity, revision: crypto.randomUUID() })
}

export async function getActiveSnapshot() {
  return idbGet("snapshots", SNAPSHOT_KEY)
}

export async function setActiveSnapshot(snapshot) {
  await idbSet("snapshots", SNAPSHOT_KEY, snapshot)
}

// 失敗後の再試行間隔。これが無いと snapshot を確定できない利用者が
// ページ遷移のたびに catalog 全URLを取り直す。
async function getSyncState() {
  try {
    return (await idbGet("snapshots", SYNC_STATE_KEY)) || {}
  } catch (_error) {
    return {}
  }
}

async function setSyncState(identity, state) {
  try {
    await idbSetForIdentity(identity, "snapshots", SYNC_STATE_KEY, state)
  } catch (_error) {
    // 状態を残せなくても同期自体は続行する
  }
}

export function isSnapshotExpired(snapshot, now = Date.now()) {
  if (!snapshot?.expiresAt) return true
  return Date.parse(snapshot.expiresAt) <= now
}

export function isStandalonePwa({
  navigatorImpl = typeof navigator !== "undefined" ? navigator : null,
  matchMediaImpl = typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? (query) => window.matchMedia(query)
    : null
} = {}) {
  if (navigatorImpl?.standalone === true) return true
  try {
    return matchMediaImpl?.("(display-mode: standalone)")?.matches === true
  } catch (_error) {
    return false
  }
}

export async function isSnapshotCacheComplete(snapshot, {
  cacheStorage = typeof caches !== "undefined" ? caches : null,
  origin = typeof window !== "undefined" ? window.location.origin : "http://localhost"
} = {}) {
  if (!cacheStorage || !Array.isArray(snapshot?.urls) || snapshot.urls.length === 0) return false
  if (snapshot.cacheName !== privateCacheName(snapshot.partition, snapshot.generation)) return false

  try {
    const cacheNames = await cacheStorage.keys()
    if (!cacheNames.includes(snapshot.cacheName)) return false

    const keys = snapshot.urls.map((url) => privateSnapshotMatchKey(snapshot, url, { origin }))
    if (keys.some((key) => !key)) return false

    const cache = await cacheStorage.open(snapshot.cacheName)
    const responses = await Promise.all(keys.map((key) => cache.match(key)))
    return responses.every(Boolean)
  } catch (_error) {
    return false
  }
}

export async function clearPrivateCaches({ keepName } = {}) {
  if (!("caches" in window)) return
  const keys = await caches.keys()
  await Promise.all(
    keys
      .filter((name) => isObsoletePrivateCache(name, keepName || null))
      .map((name) => caches.delete(name))
  )
}

export async function clearAllOfflineData() {
  syncAbortController?.abort()
  // 先に identity を失効させ、別タブで進行中の同期も書き込めなくする。
  await clearOfflineDatabase()
  await clearPrivateCaches()
  publishStatus({ state: "offline-cleared" })
}

export async function clearOfflineDataBeforeLogout({
  clearData = clearAllOfflineData,
  serviceWorker = typeof navigator !== "undefined" ? navigator.serviceWorker : null,
  timeoutMs = 1500
} = {}) {
  try {
    await clearData()
    if (serviceWorker?.controller) {
      await new Promise((resolve) => {
        const channel = new MessageChannel()
        channel.port1.onmessage = () => resolve()
        serviceWorker.controller.postMessage({ type: "CLEAR_OFFLINE_DATA" }, [channel.port2])
        setTimeout(resolve, timeoutMs)
      })
    }
  } catch (_error) {
    // ログアウト自体は継続する
  }
}

// turbo:before-fetch-request 用。preventDefault は同期で行い、cleanup 後に必ず既存 resume を呼ぶ。
export function handleLogoutBeforeFetch(event, { cleanup = clearOfflineDataBeforeLogout } = {}) {
  const fetchOptions = event?.detail?.fetchOptions
  const url = event?.detail?.url
  if (!fetchOptions || !url) return false

  const method = String(fetchOptions.method || "GET").toUpperCase()
  const path = typeof url === "string" ? url : url.pathname
  if (!(method === "DELETE" && /sign_out/.test(String(path || "")))) return false

  event.preventDefault()
  const resume = event.detail.resume
  Promise.resolve()
    .then(() => cleanup())
    .catch(() => {})
    .finally(() => {
      if (typeof resume === "function") resume()
    })
  return true
}

async function ensureStorageBudget(estimatedBytes) {
  if (!navigator.storage?.estimate) return true
  const { usage = 0, quota = 0 } = await navigator.storage.estimate()
  if (!quota) return true
  return usage + estimatedBytes < quota * 0.9
}

async function mapPool(items, concurrency, worker) {
  const results = new Array(items.length)
  let index = 0
  async function run() {
    while (index < items.length) {
      const current = index
      index += 1
      results[current] = await worker(items[current], current)
    }
  }
  const runners = Array.from({ length: Math.min(concurrency, items.length) }, () => run())
  await Promise.all(runners)
  return results
}

function isAllowedCatalogUrl(url, origin) {
  if (typeof url !== "string" || !url) return false
  let parsed
  try {
    parsed = new URL(url, origin)
  } catch (_error) {
    return false
  }
  if (parsed.origin !== origin) return false
  if (parsed.username || parsed.password) return false
  if (!parsed.pathname.startsWith("/") || parsed.pathname.startsWith("//")) return false
  if (isDeniedPath(parsed.pathname)) return false
  if (!ALLOWED_SNAPSHOT_PATH_PREFIXES.some((prefix) => (
    parsed.pathname === prefix || parsed.pathname.startsWith(`${prefix}/`)
  ))) {
    return false
  }
  return true
}

export function validateCatalogResponse(catalog, { origin } = {}) {
  const resolvedOrigin = origin || (typeof self !== "undefined" && self.location?.origin) || (typeof window !== "undefined" && window.location?.origin)
  if (!resolvedOrigin) return false
  if (!catalog || catalog.schema_version !== 1) return false
  if (typeof catalog.partition !== "string" || !OPAQUE_TOKEN_PATTERN.test(catalog.partition)) return false
  if (typeof catalog.generation !== "string" || !OPAQUE_TOKEN_PATTERN.test(catalog.generation)) return false
  if (typeof catalog.expires_at !== "string" || !Number.isFinite(Date.parse(catalog.expires_at))) return false
  if (!Array.isArray(catalog.matter_ids) || catalog.matter_ids.length > MAX_CATALOG_MATTERS) return false
  if (!catalog.matter_ids.every((id) => Number.isInteger(id) && id > 0)) return false
  if (!Array.isArray(catalog.urls)) return false
  if (!catalog.urls.every((url) => isAllowedCatalogUrl(url, resolvedOrigin))) return false
  return true
}

function sleep(ms) {
  if (!ms) return Promise.resolve()
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function retryDelay(response) {
  const value = response.headers.get("Retry-After")
  const seconds = value && Number(value)
  const delay = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(value) - Date.now()
  return Math.max(RETRY_BACKOFF_MS, Number.isFinite(delay) ? delay : 0)
}

// 取得できたものだけで snapshot を確定する。予算超過とサーバ側スロットルは打ち切り、
// 個別失敗はその URL を落とすだけに留め、1件の失敗で全件を捨てない。
// 1件ずつ間隔を空けて取りに行く。ここを並列化・高速化すると本番 DB を落とす。
export async function fetchAndStoreSnapshots({
  catalog,
  cacheName,
  signal,
  isCurrent = async () => true,
  maxBytes = MAX_SNAPSHOT_BYTES,
  delayMs = FETCH_DELAY_MS,
  reuseUrls = [],
  onProgress = () => {}
}) {
  let totalBytes = 0
  let budgetExhausted = false
  let throttled = false
  let retryAfter = 0
  let started = 0
  const failures = []
  const storedUrls = []
  const skipped = []

  // 同世代の修復では保存済みの本文を再取得しない。後方のURLも先に数え、
  // 補完分を足して25MBを超えないようにする。
  const cache = await caches.open(cacheName)
  const reusable = new Set(reuseUrls)
  const stored = new Set()
  for (const url of catalog.urls) {
    if (signal?.aborted || !(await isCurrent())) break
    const key = normalizeOfflineUrl(url, self.location?.origin || window.location.origin)
    if (!reusable.has(key)) continue
    const response = await cache.match(key)
    if (!response || !canStoreNavigationResponse(response, { expectedPartition: catalog.partition })) continue
    const bytes = (await response.arrayBuffer()).byteLength
    if (totalBytes + bytes > maxBytes) continue
    totalBytes += bytes
    stored.add(key)
    storedUrls.push(key)
  }

  onProgress({ done: storedUrls.length, total: catalog.urls.length })
  await mapPool(catalog.urls, FETCH_CONCURRENCY, async (url) => {
    const requestUrl = normalizeOfflineUrl(url, self.location?.origin || window.location.origin)
    if (stored.has(requestUrl)) return
    if (budgetExhausted || throttled) {
      skipped.push({ url, reason: throttled ? "throttled" : "over-budget" })
      return
    }
    try {
      if (signal?.aborted || !(await isCurrent())) throw new DOMException("Aborted", "AbortError")
      if (started > 0) await sleep(delayMs)
      if (signal?.aborted || !(await isCurrent())) throw new DOMException("Aborted", "AbortError")
      started += 1
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "text/html", [OFFLINE_SYNC_HEADER]: "1" },
        signal
      })
      // サーバが絞ってきたら以降は取りに行かない。取得済みだけで確定する。
      if (response.status === 429 || response.status >= 500) {
        throttled = true
        retryAfter = Date.now() + retryDelay(response)
        skipped.push({ url, reason: "throttled" })
        return
      }
      if (
        !canStoreNavigationResponse(response, { expectedPartition: catalog.partition }) ||
        response.headers.get("Turbo-Frame")
      ) {
        failures.push({ url, reason: "not-cacheable" })
        return
      }
      const clone = response.clone()
      const buffer = await clone.arrayBuffer()
      if (totalBytes + buffer.byteLength > maxBytes) {
        budgetExhausted = true
        skipped.push({ url, reason: "over-budget" })
        return
      }
      totalBytes += buffer.byteLength
      if (signal?.aborted || !(await isCurrent())) throw new DOMException("Aborted", "AbortError")
      await cache.put(requestUrl, new Response(buffer, {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers
      }))
      storedUrls.push(requestUrl)
    } catch (error) {
      failures.push({ url, reason: error?.name || "fetch-failed" })
      // 通信断やストレージ障害中に残りの全URLを取りに行かない。
      throttled = true
      retryAfter = Date.now() + RETRY_BACKOFF_MS
    } finally {
      onProgress({ done: storedUrls.length, total: catalog.urls.length })
    }
  })

  return { totalBytes, failures, storedUrls, skipped, throttled, retryAfter, budgetExhausted }
}

let syncInFlight = null
let syncAbortController = null
let syncProgress = null

export async function getOfflineSyncStatus() {
  const [identity, snapshot, state] = await Promise.all([getIdentity(), getActiveSnapshot(), getSyncState()])
  return { identity, snapshot, retryAfter: state.retryAfter || 0, syncing: !!syncInFlight, progress: syncProgress }
}

export async function syncOfflineSnapshot({ force = false } = {}) {
  if (syncInFlight) return syncInFlight

  const controller = new AbortController()
  syncAbortController = controller
  const { signal } = controller

  const runSync = async () => {
    const identity = await getIdentity()
    const isCurrent = async () => {
      const current = await getIdentity()
      return !signal.aborted && current.enabled && current.revision === identity.revision
    }
    const cancelled = { ok: false, reason: "cancelled" }
    if (!identity.enabled) {
      publishStatus({ state: "offline-disabled" })
      return { ok: false, reason: "disabled" }
    }
    if (navigator.onLine === false) return { ok: false, reason: "offline" }

    const active = await getActiveSnapshot()
    const syncState = await getSyncState()
    if (syncState.retryAfter && Date.now() < syncState.retryAfter) {
      return { ok: false, reason: "backoff", snapshot: active }
    }
    if (
      !force &&
      active &&
      !active.retryable &&
      !isSnapshotExpired(active) &&
      active.syncedAt &&
      Date.now() - Date.parse(active.syncedAt) < SYNC_INTERVAL_MS &&
      await isSnapshotCacheComplete(active)
    ) {
      return { ok: true, reason: "fresh", snapshot: active }
    }

    publishStatus({ state: "syncing" })

    // 開始前に保存するので、途中の例外やリロードでも即時に全件を再取得しない。
    // 手動の準備ボタンでも障害時の待機時間は回避させない。
    await setSyncState(identity, { retryAfter: Date.now() + RETRY_BACKOFF_MS })

    let catalog
    try {
      const response = await fetch("/offline/catalog", {
        credentials: "same-origin",
        headers: { Accept: "application/json", [OFFLINE_SYNC_HEADER]: "1" },
        signal
      })
      if (response.status === 401) {
        publishStatus({ state: "auth-expired" })
        return { ok: false, reason: "auth" }
      }
      if (!response.ok) {
        await setSyncState(identity, { retryAfter: Date.now() + retryDelay(response) })
        publishStatus({ state: "sync-failed", snapshot: active })
        return { ok: false, reason: "catalog-http" }
      }
      catalog = await response.json()
    } catch (error) {
      if (!(await isCurrent())) return cancelled
      publishStatus({ state: "sync-failed", snapshot: active })
      return { ok: false, reason: "catalog-network" }
    }

    if (!(await isCurrent())) return cancelled

    if (!validateCatalogResponse(catalog)) {
      publishStatus({ state: "sync-failed", snapshot: active })
      return { ok: false, reason: "catalog-invalid" }
    }

    if (identity.partition && identity.partition !== catalog.partition) {
      await clearAllOfflineData()
      await setIdentity({ enabled: false, partition: null })
      publishStatus({ state: "offline-disabled", reason: "partition-changed" })
      return { ok: false, reason: "partition-changed" }
    }

    if (!(await idbSetForIdentity(identity, "identity", IDENTITY_KEY, { ...identity, partition: catalog.partition }))) {
      return cancelled
    }

    // 世代が同じならクロールしない。ただし期限を伸ばさないと、同世代のまま
    // 永久に「期限切れ」表示になり毎回 catalog を取りに来てしまう。
    const sameGeneration = active?.generation === catalog.generation && active.partition === catalog.partition
    const hasAllUrls = catalog.urls.every((url) => active?.urls?.includes(
      normalizeOfflineUrl(url, window.location.origin)
    ))
    if (sameGeneration && (hasAllUrls || (active.budgetExhausted && !active.retryable)) &&
        await isSnapshotCacheComplete({ ...active, expiresAt: catalog.expires_at })) {
      const refreshed = {
        ...active,
        expiresAt: catalog.expires_at,
        syncedAt: new Date().toISOString()
      }
      if (!(await idbSetForIdentity(identity, "snapshots", SNAPSHOT_KEY, refreshed))) return cancelled
      await setSyncState(identity, { retryAfter: 0 })
      publishStatus({ state: "synced", snapshot: refreshed })
      return { ok: true, reason: "same-generation", snapshot: refreshed }
    }

    const estimated = Math.min(MAX_SNAPSHOT_BYTES, Math.max(catalog.urls.length * 50_000, 1))
    if (!(await ensureStorageBudget(estimated))) {
      publishStatus({ state: "sync-failed", snapshot: active, reason: "quota" })
      return { ok: false, reason: "quota", snapshot: active }
    }

    const cacheName = privateCacheName(catalog.partition, catalog.generation)
    const { totalBytes, failures, storedUrls, skipped, retryAfter, budgetExhausted } = await fetchAndStoreSnapshots({
      catalog,
      cacheName,
      signal,
      isCurrent,
      reuseUrls: sameGeneration ? active.urls : [],
      onProgress: (progress) => {
        syncProgress = progress
        window.dispatchEvent(new CustomEvent(OFFLINE_SYNC_EVENT))
      }
    })

    if (retryAfter) await setSyncState(identity, { retryAfter })

    if (!(await isCurrent())) {
      await caches.delete(cacheName)
      return cancelled
    }

    // ランディングが無いとオフライン起動そのものが成立しないので、ここだけは必須にする。
    // セッション切れで全ページが not-cacheable になった場合もここで弾かれ、旧 snapshot が残る。
    const landingUrl = normalizeOfflineUrl(OFFLINE_LANDING_URL, window.location.origin)
    if (!storedUrls.includes(landingUrl)) {
      if (!sameGeneration) await caches.delete(cacheName)
      publishStatus({ state: "sync-failed", snapshot: active, failureCount: failures.length })
      return { ok: false, reason: "landing-missing", failures, snapshot: active }
    }

    const snapshot = {
      generation: catalog.generation,
      partition: catalog.partition,
      expiresAt: catalog.expires_at,
      syncedAt: new Date().toISOString(),
      urls: storedUrls,
      urlCount: catalog.urls.length,
      storedCount: storedUrls.length,
      skippedCount: skipped.length,
      failureCount: failures.length,
      budgetExhausted,
      retryable: failures.length > 0 || retryAfter > 0,
      matterCount: Array.isArray(catalog.matter_ids) ? catalog.matter_ids.length : 0,
      byteSize: totalBytes,
      cacheName
    }

    if (!(await idbSetForIdentity(identity, "snapshots", SNAPSHOT_KEY, snapshot))) {
      await caches.delete(cacheName)
      return cancelled
    }
    await clearPrivateCaches({ keepName: cacheName })
    await setSyncState(identity, {
      retryAfter: snapshot.retryable ? Math.max(retryAfter, Date.now() + RETRY_BACKOFF_MS) : 0
    })
    publishStatus({ state: "synced", snapshot })
    return { ok: true, snapshot }
  }

  syncInFlight = runSync()

  try {
    return await syncInFlight
  } finally {
    syncInFlight = null
    syncAbortController = null
    syncProgress = null
    window.dispatchEvent(new CustomEvent(OFFLINE_SYNC_EVENT))
  }
}

export async function enableOfflineStorage() {
  const identity = await getIdentity()
  // 既に有効ならrevisionを更新しない。準備ボタンで進行中の自動同期を失効させない。
  if (!identity.enabled) {
    await setIdentity({ enabled: true, partition: identity.partition, disabledByUser: false })
  }
  if (navigator.storage?.persist) {
    try {
      await navigator.storage.persist()
    } catch (_error) {
      // persist 拒否でも継続
    }
  }
  return syncOfflineSnapshot({ force: true })
}

// 自動同期はホーム画面から起動した PWA のときだけ行う。インストール済み PWA と
// 通常タブは同一オリジンの IndexedDB を共有するため、有効化済みかどうかだけで
// 判定すると通常のブラウザ利用中も catalog 全URLを取りに行ってしまう。
export async function syncOfflineIfNeeded({
  identity,
  standalone = isStandalonePwa(),
  sync = syncOfflineSnapshot,
  enable = enableOfflineStorage
} = {}) {
  if (!standalone) return { ok: false, reason: "not-standalone" }

  const resolvedIdentity = identity || await getIdentity()
  if (resolvedIdentity.enabled) return sync()
  if (!resolvedIdentity.disabledByUser) return enable()
  return { ok: false, reason: "disabled" }
}

export async function disableOfflineStorage() {
  await clearAllOfflineData()
  await setIdentity({ enabled: false, partition: null, disabledByUser: true })
  publishStatus({ state: "offline-disabled" })
}

export function startOfflineSyncScheduler({ standalone = isStandalonePwa() } = {}) {
  // 通常のブラウザタブではリスナすら張らない。
  if (!standalone) return

  // 鮮度とバックオフの判定は syncOfflineSnapshot 側に一本化する。
  // ここで独自に判定すると、snapshot を確定できない利用者が遷移のたびに再クロールする。
  const run = () => {
    syncOfflineIfNeeded({ standalone: true }).catch(() => {})
  }

  run()
  window.addEventListener("online", run)
  document.addEventListener("turbo:load", run)
}

// headers used by SW / tests
export { OFFLINE_CACHEABLE_HEADER, OFFLINE_SUBJECT_HEADER }
